import { useCallback, useEffect, useState } from 'react'
import { DEFAULT_FORECAST_YEARS, MAX_FORECAST_YEARS } from '../utils/getResourceUsageForecast'

// The forecast window the player picked in the mine popup. Persisted so it
// survives panel unmounts (phone tabs) and sessions, and broadcast so every
// view showing a deficit/surplus (mine popup, galactic trader) agrees on it.
export const FORECAST_YEARS_KEY = 'pg-forecast-years'
export const FORECAST_YEARS_EVENT = 'pg-forecast-years-change'

export const clampForecastYears = (value: unknown) => {
  const years = Math.floor(Number(value))
  if (!Number.isFinite(years) || years < 1) {
    return DEFAULT_FORECAST_YEARS
  }
  return Math.min(years, MAX_FORECAST_YEARS)
}

export const readForecastYears = () => {
  try {
    const raw = localStorage.getItem(FORECAST_YEARS_KEY)
    return raw === null ? DEFAULT_FORECAST_YEARS : clampForecastYears(raw)
  } catch {
    return DEFAULT_FORECAST_YEARS
  }
}

export const writeForecastYears = (years: number) => {
  try {
    localStorage.setItem(FORECAST_YEARS_KEY, String(years))
  } catch {
    // Private browsing without storage — the in-memory state still works.
  }
}

export const useForecastYears = (): [number, (years: number) => void] => {
  const [years, setYears] = useState<number>(readForecastYears)

  useEffect(() => {
    const onChange = () => setYears(readForecastYears())
    window.addEventListener(FORECAST_YEARS_EVENT, onChange)
    window.addEventListener('storage', onChange)
    return () => {
      window.removeEventListener(FORECAST_YEARS_EVENT, onChange)
      window.removeEventListener('storage', onChange)
    }
  }, [])

  const selectYears = useCallback((next: number) => {
    const clamped = clampForecastYears(next)
    writeForecastYears(clamped)
    setYears(clamped)
    window.dispatchEvent(new Event(FORECAST_YEARS_EVENT))
  }, [])

  return [years, selectYears]
}
